import { useEffect, useMemo, useState } from 'react';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import { TailwindWrapper } from '@utils/TailwindWrapper';

import { Text, RadioButton, Button } from '@ui-component-library';

import { usePropsDataStore } from '../../store/PropsDataStore';

import {
  SELL_TIMBER_ACTION_INFO,
  SELL_TIMBER_FIELD_INFO,
  SELL_TIMBER_SALE_INFO,
  SellTimberFieldInfo
} from './components/SellTimberFieldInfo.js';
import { loadSellTimberMapPropertyBundle } from './helpers/sellTimberMapPropertyLoad';
import {
  ACTION_OPTIONS,
  isGrotArrangement,
  SALE_OPTIONS
} from './helpers/sellTimberShared';

const SV_LONG_TODAY_FORMATTER = new Intl.DateTimeFormat('sv-SE', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});

export default function SellTimberStep1({ wizard, dispatch }) {
  const initialData = wizard.adCreateData;

  const activeProps = usePropsDataStore((s) => s.activeProps);

  const selectedProperty = useMemo(() => {
    if (activeProps.length !== 1) return null;
    const p = activeProps[0];
    return {
      ref: String(p.ref),
      designation: String(p.designation ?? '')
    };
  }, [activeProps]);

  const selectedIsShared = useMemo(() => {
    if (activeProps.length !== 1) return false;
    const p = activeProps[0];
    return p?.role ? p.role !== 'owner' : Boolean(p?.is_shared);
  }, [activeProps]);

  const propertySelectionHint =
    activeProps.length === 0
      ? 'none'
      : activeProps.length > 1
        ? 'multiple'
        : selectedIsShared
          ? 'shared'
          : null;

  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);
  const todayFormatted = SV_LONG_TODAY_FORMATTER.format(now);

  const [selectedAction, setSelectedAction] = useState(initialData?.selectedAction ?? '');
  const [selectedSale, setSelectedSale] = useState(initialData?.selectedSale ?? '');
  const [stepError, setStepError] = useState(null);

  const isGrot = isGrotArrangement(selectedAction);

  /** Preload step 2 map data while the user fills the form (same ref as fastighetsväljaren). */
  useEffect(() => {
    const ref = selectedProperty?.ref;
    if (!ref) return undefined;

    const cache = wizard.mapPropertyCache;
    if (cache && String(cache.ref) === String(ref)) {
      if (cache.propertyFetch?.status === 'success' || cache.propertyFetch?.status === 'loading') {
        return undefined;
      }
    }

    dispatch({ type: 'MAP_PROPERTY_CACHE_LOADING', ref });
    void loadSellTimberMapPropertyBundle(ref).then((bundle) => {
      dispatch({ type: 'MAP_PROPERTY_CACHE_SET', payload: bundle });
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when property ref changes; cache is read to avoid duplicate fetches
  }, [dispatch, selectedProperty?.ref]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedIsShared) {
      setStepError(
        'Endast fastighetens ägare kan skapa en annons. Den här fastigheten är delad med dig.'
      );
      return;
    }
    const missing = [];
    if (!selectedProperty?.ref) {
      missing.push(
        propertySelectionHint === 'multiple'
          ? 'välj endast en fastighet i fastighetsväljaren'
          : 'välj en fastighet i fastighetsväljaren'
      );
    }
    if (!selectedAction) {
      missing.push('välj typ av åtgärd');
    }
    if (!isGrot && !selectedSale) {
      missing.push('välj hur du säljer virket');
    }
    if (missing.length > 0) {
      setStepError(
        `Fyll i allt som krävs innan du går vidare: \n- ${missing.join('\n- ')}`
      );
      return;
    }
    const end_time =
      initialData?.end_time && String(initialData.end_time).trim()
        ? String(initialData.end_time).trim()
        : format(addDays(now, 21), 'yyyy-MM-dd');
    setStepError(null);
    dispatch({
      type: 'SET_AD_CORE',
      payload: {
        ...(initialData ?? {}),
        selectedProperty,
        selectedAction,
        selectedSale: isGrot ? null : selectedSale,
        end_time,
        comments: initialData?.comments ?? '',
        grotCommentsExtra: initialData?.grotCommentsExtra ?? '',
        attachments: initialData?.attachments ?? [],
        start_time: initialData?.start_time ?? todayIso,
        startTimeFormatted: initialData?.startTimeFormatted ?? todayFormatted
      }
    });
    dispatch({ type: 'SET_STEP', step: 2 });
  };

  return (
    <>
      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <form
            className="mx-auto min-w-full flex max-w-full flex-col gap-8 p-6"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="flex flex-col gap-2">
              <Text variant="text-xl/leading-normal/medium">Sälj virke</Text>
              <Text variant="text-sm/leading-normal/medium" className="text-charcoal-700">
                Fyll i formuläret nedan för att enkelt ta emot anbud från flera köpare.
              </Text>
              <Text variant="text-sm/leading-normal/medium" className="text-charcoal-700">
                Köparna kommer få tillgång till ditt telefonnummer och din e-post för att kontakta dig.
              </Text>
            </div>

            <div className="flex flex-col gap-2">
              <Text variant="text-sm/leading-normal/medium" className="mb-1">
                Fastighetsbeteckning
              </Text>
              {selectedIsShared ? (
                <Text
                  variant="text-base/leading-normal/normal"
                  className="text-red-700"
                >
                  Den här fastigheten är delad med dig. Endast fastighetens ägare kan skapa en
                  annons.
                </Text>
              ) : selectedProperty ? (
                <Text variant="text-base/leading-normal/normal" className="capitalize">
                  {selectedProperty.designation}
                </Text>
              ) : propertySelectionHint === 'multiple' ? (
                <Text
                  variant="text-base/leading-normal/normal"
                  className="text-red-700"
                >
                  Flera fastigheter är valda. Välj endast en fastighet i fastighetsväljaren för att
                  fortsätta.
                </Text>
              ) : (
                <Text
                  variant="text-base/leading-normal/normal"
                  className="text-red-700"
                >
                  Välj en fastighet i fastighetsväljaren överst på sidan.
                </Text>
              )}
            </div>

            <fieldset className="flex flex-col gap-3 border-0 p-0">
              <legend
                id="legend-action"
                className="mb-2 flex w-full max-w-full flex-wrap items-center gap-2 border-0 p-0"
              >
                <Text variant="text-sm/leading-normal/medium" as="span">
                  Typ av åtgärd
                </Text>
              </legend>
              <div className="flex flex-col gap-3" role="radiogroup" aria-labelledby="legend-action">
                {ACTION_OPTIONS.map(({ value, label }) => {
                  const actionHelp = SELL_TIMBER_ACTION_INFO[value];
                  return (
                    <RadioButton
                      key={value}
                      name="arrangement"
                      value={value}
                      checked={selectedAction === value}
                      onClick={(v) => {
                        setStepError(null);
                        setSelectedAction(v);
                        if (v === 'grot') {
                          setSelectedSale('');
                        }
                        if (v === 'plockhuggning'){
                          setSelectedSale('avverkningsuppdrag')
                        }
                      }}
                    >
                      <div className="flex min-w-0 flex-1 flex-row items-center gap-3 lg:inline-flex lg:w-fit lg:max-w-full lg:flex-none lg:gap-2">
                        <Text
                          variant="text-sm/leading-normal/normal"
                          className="min-w-0 flex-1 text-left [overflow-wrap:anywhere] lg:flex-none lg:max-w-[min(100%,52rem)]"
                        >
                          {label}
                        </Text>
                        <span
                          className="inline-flex shrink-0 self-center"
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <SellTimberFieldInfo
                            title={actionHelp.title}
                            text={actionHelp.text}
                            aria-label={`Information om ${label}`}
                          />
                        </span>
                      </div>
                    </RadioButton>
                  );
                })}
              </div>
            </fieldset>

            {!isGrot ? (
              <fieldset className="flex flex-col gap-3 border-0 p-0">
                <legend
                  id="legend-sale"
                  className="mb-2 flex w-full max-w-full flex-wrap items-center gap-2 border-0 p-0"
                >
                  <Text variant="text-sm/leading-normal/medium" as="span">
                    Hur vill du sälja ditt virke?
                  </Text>
                </legend>
                <div className="flex flex-col gap-3" role="radiogroup" aria-labelledby="legend-sale">
                  {selectedAction === 'plockhuggning' && <Text variant="text-sm/leading-normal/medium" className="text-success-700">All plockhuggning säljs som avverkningsuppdrag</Text>}
                  {SALE_OPTIONS.map(({ value, label }) => {
                    const saleHelp = SELL_TIMBER_SALE_INFO[value];
                    return (
                      <RadioButton
                        key={value}
                        name="type"
                        value={value}
                        checked={selectedSale === value}
                        disabled={selectedAction === 'plockhuggning'}
                        onClick={(v) => {
                          setStepError(null);
                          setSelectedSale(v);
                        }}
                      >
                        <div className="flex min-w-0 flex-1 flex-row items-center gap-3 lg:inline-flex lg:w-fit lg:max-w-full lg:flex-none lg:gap-2">
                          <Text
                            variant="text-sm/leading-normal/normal"
                            className="min-w-0 flex-1 text-left [overflow-wrap:anywhere] lg:flex-none lg:max-w-[min(100%,52rem)]"
                          >
                            {label}
                          </Text>
                          <span
                            className="inline-flex shrink-0 self-center"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <SellTimberFieldInfo
                              title={saleHelp.title}
                              text={saleHelp.text}
                              aria-label={`Information om ${label}`}
                            />
                          </span>
                        </div>
                      </RadioButton>
                    );
                  })}
                </div>
              </fieldset>
            ) : null}

            <div className="flex flex-col gap-2">
              <Text variant="text-sm/leading-normal/medium">Annonsperiod</Text>
              <Text variant="text-sm/leading-normal/medium" className="text-charcoal-700">
                Din annons ligger synlig i tre veckor från och med dagen Treebula publicerar den.
              </Text>
            </div>

            {stepError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700 whitespace-pre-wrap" role="alert">
                {stepError}
              </Text>
            ) : null}

            <Button type="submit" variant="primary">
              Nästa
            </Button>
          </form>
        </div>
      </TailwindWrapper>
    </>
  );
}
